/**
 * Framework-agnostic toast bus.
 *
 * A module-level event emitter so that NON-React code (the axios interceptor,
 * the React Query global error handler) can surface user-facing notifications,
 * while React components consume them via <ToastHost> / useToast().
 */

export type ToastSeverity = 'success' | 'error' | 'info' | 'warning';

export interface ToastOptions {
  message: string;
  severity?: ToastSeverity;
  /** Auto-hide duration in ms. null = sticky until dismissed. */
  duration?: number | null;
}

export interface ToastRecord extends Required<Omit<ToastOptions, 'duration'>> {
  id: number;
  duration: number | null;
}

type Listener = (toast: ToastRecord) => void;

let listeners: Listener[] = [];
let counter = 0;

export const subscribeToast = (listener: Listener): (() => void) => {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
};

// Dedupe window: identical message+severity fired in rapid succession (e.g. two
// queries on one page failing with the same backend error, or a poll cycle
// re-failing) collapse into a single toast instead of stacking duplicates.
const DEDUPE_WINDOW_MS = 2_500;
let lastKey = '';
let lastId = 0;
let lastAt = 0;

export const toast = (input: ToastOptions | string): number => {
  const opts: ToastOptions = typeof input === 'string' ? { message: input } : input;
  const severity = opts.severity ?? 'info';
  const key = `${severity}:${opts.message}`;
  const now = Date.now();
  if (key === lastKey && now - lastAt < DEDUPE_WINDOW_MS) {
    lastAt = now;
    return lastId;
  }
  const record: ToastRecord = {
    id: ++counter,
    message: opts.message,
    severity,
    duration: opts.duration === undefined ? 4000 : opts.duration,
  };
  lastKey = key;
  lastId = record.id;
  lastAt = now;
  listeners.forEach((l) => l(record));
  return record.id;
};

export const toastSuccess = (message: string) => toast({ message, severity: 'success' });
export const toastError = (message: string) => toast({ message, severity: 'error', duration: 6000 });
export const toastInfo = (message: string) => toast({ message, severity: 'info' });
export const toastWarning = (message: string) => toast({ message, severity: 'warning' });

/**
 * Normalize an axios/unknown error into a human-readable message.
 */
export const getApiErrorMessage = (error: unknown, fallback = 'Something went wrong'): string => {
  const anyErr = error as any;
  return (
    anyErr?.response?.data?.message ||
    anyErr?.response?.data?.error ||
    anyErr?.message ||
    fallback
  );
};
